/**
 * Client-side pre-check that mirrors Hailer's vehicle/driver availability rule
 * for fixed-route shift solutions.
 *
 * Hailer rejects a `createshiftsolution` whose vehicle (or driver) already has a
 * shift solution with an OVERLAPPING `[startDate, endDate]` window, returning
 * `success: false, error: "Vehicle not available at this time"`
 * (see hailer .../create-shift-solution/usecase.ts). That rule is correct but
 * surfaces as an opaque per-shift failure on the two most common flows:
 *   1. Re-uploading a plan whose vehicles were already deployed on an earlier run.
 *   2. Reusing one vehicle across several routes/days that share the plan's date
 *      range (every shift after the first collides with the first).
 *
 * Running the same comparison BEFORE we POST lets us skip those shifts with a
 * clear "already deployed" message instead of a raw rejection, and makes
 * re-uploads idempotent. It is a best-effort aid only — the live write remains
 * the source of truth, so if we can't fetch the existing bookings we simply
 * don't pre-check and let Hailer decide.
 */

/** A vehicle booking that already exists in Ops (one active shift solution). */
export interface ExistingVehicleBooking {
  vehicleId: string;
  startDate: string;
  endDate: string;
  operationName?: string;
  routeLabel?: string;
  shiftSolutionId?: string;
}

/**
 * Loose shape of an operations-solutions entry's shift solution as returned by
 * Hailer's `getoperationssolutionsall` query view
 * (hailer .../operations/get-solutions-all: OperationsSolutionsShiftSolution).
 * Typed permissively because the client's `UATShiftSolution` interface models
 * the on-demand shape, not the fixed-route one carrying startDate/endDate.
 */
export interface OpsShiftSolutionLike {
  id?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  vehicle?: { id?: string; name?: string } | null;
  route?: { label?: string } | null;
}

export interface OpsOperationSolutionLike {
  name?: string;
  shiftSolutions?: OpsShiftSolutionLike[] | null;
}

/** Parse an API/wall-time datetime string to epoch ms, or null when unusable. */
function toEpochMs(value: string | undefined | null): number | null {
  if (!value || typeof value !== 'string') return null;
  const ms = Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Inclusive range overlap, mirroring Hailer's gte/lte window check. Returns
 * `false` (no conflict) when either range can't be parsed — we never want a bad
 * timestamp to spuriously skip a legitimate shift.
 */
export function datesOverlap(
  aStart: string | undefined,
  aEnd: string | undefined,
  bStart: string | undefined,
  bEnd: string | undefined,
): boolean {
  const as = toEpochMs(aStart);
  const ae = toEpochMs(aEnd);
  const bs = toEpochMs(bStart);
  const be = toEpochMs(bEnd);
  if (as === null || ae === null || bs === null || be === null) return false;
  return as <= be && bs <= ae;
}

/**
 * Flatten operations-solutions entries into the set of existing vehicle bookings
 * (one per ACTIVE shift solution that has a vehicle and a usable date range).
 */
export function extractExistingVehicleBookings(
  operations: OpsOperationSolutionLike[] | undefined | null,
): ExistingVehicleBooking[] {
  const bookings: ExistingVehicleBooking[] = [];
  for (const operation of operations ?? []) {
    for (const shift of operation?.shiftSolutions ?? []) {
      const vehicleId = shift?.vehicle?.id?.trim();
      if (!vehicleId) continue;
      // A cancelled/inactive solution no longer books the vehicle.
      if (shift.status && shift.status !== 'active') continue;
      if (toEpochMs(shift.startDate) === null || toEpochMs(shift.endDate) === null) continue;
      bookings.push({
        vehicleId,
        startDate: shift.startDate as string,
        endDate: shift.endDate as string,
        operationName: operation?.name,
        routeLabel: shift.route?.label,
        shiftSolutionId: shift.id,
      });
    }
  }
  return bookings;
}

/**
 * The first existing booking that conflicts with `request` — same vehicle AND an
 * overlapping date window — or null when the vehicle is free (or no vehicle is
 * set, in which case there's nothing to pre-check).
 */
export function findConflictingBooking(
  request: { vehicleId?: string; startDate?: string; endDate?: string },
  bookings: ExistingVehicleBooking[],
): ExistingVehicleBooking | null {
  const vehicleId = request.vehicleId?.trim();
  if (!vehicleId) return null;
  for (const booking of bookings) {
    if (booking.vehicleId !== vehicleId) continue;
    if (datesOverlap(request.startDate, request.endDate, booking.startDate, booking.endDate)) {
      return booking;
    }
  }
  return null;
}

/** Human-readable skip reason for a vehicle that's already booked. */
export function describeBookingConflict(
  vehicleLabel: string,
  booking: ExistingVehicleBooking,
): string {
  const where = booking.operationName ? ` in "${booking.operationName}"` : '';
  return `Vehicle ${vehicleLabel} already has a shift for these dates${where} — skipped (already deployed).`;
}
